import { Alert, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Route } from 'lucide-react';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import { useJobTrips } from '../hooks/use-job-trips';
import { gpsDistanceLabel } from '../policies/gps-distance-label';
import type { JobTrip } from '../trips.api';
import { TripCrossings } from './TripCrossings';

function GpsDistanceCell({ trip }: { trip: JobTrip }) {
  const label = gpsDistanceLabel(trip.gpsDistance);
  return (
    <span className="inline-flex items-center gap-2">
      <span className={trip.gpsDistance ? undefined : 'text-muted'}>{label.text}</span>
      {label.isLowCoverage && <Tag color="warning">Low coverage</Tag>}
    </span>
  );
}

const COLUMNS: ColumnsType<JobTrip> = [
  { title: 'Trip', dataIndex: 'number', key: 'number' },
  { title: 'Status', dataIndex: 'status', key: 'status' },
  { title: 'From', dataIndex: 'originZoneCode', key: 'origin', render: (code: string | null) => code ?? '—' },
  { title: 'To', dataIndex: 'destinationZoneCode', key: 'destination', render: (code: string | null) => code ?? '—' },
  { title: 'GPS distance', key: 'gpsDistance', render: (_, trip) => <GpsDistanceCell trip={trip} /> },
];

/** The job's trips, read-only: planning and assignment live on dispatch. Not server-paged, because a job has a few trips. */
export function JobTripsTab({ jobId }: { jobId: string }) {
  const state = useJobTrips(jobId);
  if (state.status === 'loading') return <TableSkeleton label="Loading trips" rows={3} />;
  if (state.status === 'error') return <Alert type="error" showIcon title="Could not load this job's trips. Try again in a moment." />;
  if (state.trips.length === 0)
    return <EmptyState icon={Route} title="No trips yet" description="Trips appear here once the job has been planned onto them." />;
  return (
    <Table<JobTrip>
      rowKey="id"
      columns={COLUMNS}
      dataSource={state.trips}
      pagination={false}
      scroll={{ x: 'max-content' }}
      expandable={{
        expandedRowRender: (trip) => <TripCrossings tripId={trip.id} />,
        rowExpandable: (trip) => trip.status !== 'pending' && trip.status !== 'assigned',
      }}
    />
  );
}
